import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { fmtDateTime } from '../../lib/datetime';

// Admin console — hub page containing User Management. Lists all members with
// tier/status/verification/login-audit, supports filtering, bulk actions on
// selected users, and a per-user detail modal (incl. IP addresses).
//
// SAFETY: granting/revoking ADMIN is intentionally NOT a bulk or quick action —
// it is only available inside the single-user detail modal, behind an explicit
// confirmation, so no one is made admin by mistake.
//
// Security: this page is UX only. Every action calls an admin-gated server route
// (/api/admin/*) which authoritatively re-checks is_admin from the DB.

const Badge = ({ color, children }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${color}`}>{children}</span>
);

// membership-api serializes LocalDateTime as a NAIVE (UTC) timestamp without a
// timezone marker; fmtDateTime parses it as UTC and renders local + zone label.
const fmtDate = (d) => fmtDateTime(d);

// Clickable, sortable column header. Shows ▲/▼ when it is the active sort column.
const SortHeader = ({ label, sortKey: colKey, activeKey, dir, onSort, className = '' }) => {
  const active = activeKey === colKey;
  return (
    <th className={`px-3 py-2 ${className}`}>
      <button
        type="button"
        onClick={() => onSort(colKey)}
        className={`inline-flex items-center gap-1 font-medium hover:text-gray-900 ${active ? 'text-gray-900' : 'text-gray-600'}`}
        title={`Sort by ${label}`}
      >
        <span>{label}</span>
        <span className="text-[10px] leading-none w-2">{active ? (dir === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
};
// Normalize the admin flag regardless of how the API serializes it
// (membership-api returns the boolean getter isAdmin() as JSON "admin").
const isAdminFlag = (m) => m?.isAdmin === true || m?.admin === true;

const AdminUsers = ({ embedded = false }) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  // Filters
  const [tierFilter, setTierFilter] = useState('ALL');       // ALL | PREMIUM | FREE
  const [statusFilter, setStatusFilter] = useState('ALL');   // ALL | ACTIVE | PENDING
  const [verifiedFilter, setVerifiedFilter] = useState('ALL'); // ALL | YES | NO
  const [adminFilter, setAdminFilter] = useState('ALL');     // ALL | YES | NO

  // Selection (by id) for bulk actions.
  const [selected, setSelected] = useState(() => new Set());

  // Detail modal
  const [detail, setDetail] = useState(null); // member object | null

  // Column sorting. Default: Created descending (newest first).
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

  const onSort = (key) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        // same column -> toggle direction
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      // new column -> default to ascending, except dates/counts default desc (most useful)
      setSortDir(['createdAt', 'lastLoginAt', 'loginCount'].includes(key) ? 'desc' : 'asc');
      return key;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        axios.get('/admin/members'),
        axios.get('/admin/stats'),
      ]);
      setMembers(Array.isArray(m.data) ? m.data : []);
      setStats(s.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchMember = async (id, fields, successMsg) => {
    setBusy(true);
    try {
      await axios.patch(`/admin/members/${id}`, fields);
      if (successMsg) toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const deleteMember = async (id, email) => {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`/admin/members/${id}`);
      toast.success('Member deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  // --- Admin grant/revoke: isolated, modal-only, confirmed. ---
  const toggleAdmin = async (m) => {
    if (String(m.id) === String(user?.id)) {
      toast.error('You cannot change your own admin flag.');
      return;
    }
    const granting = !isAdminFlag(m);
    const msg = granting
      ? `Grant ADMIN access to ${m.email}?\n\nThey will be able to manage ALL users (change tiers, delete accounts, grant admin). Only do this for people you fully trust.`
      : `Revoke admin access from ${m.email}?`;
    if (!window.confirm(msg)) return;
    try {
      await patchMember(m.id, { isAdmin: granting }, granting ? 'Admin access granted' : 'Admin access revoked');
      setDetail((d) => (d && d.id === m.id ? { ...d, isAdmin: granting, admin: granting } : d));
    } catch (_) { /* toast already shown */ }
  };

  const filtered = useMemo(() => members.filter((m) => {
    if (tierFilter !== 'ALL' && (m.membershipType || 'FREE') !== tierFilter) return false;
    if (statusFilter !== 'ALL' && (m.status || '') !== statusFilter) return false;
    if (verifiedFilter !== 'ALL' && (!!m.emailVerified) !== (verifiedFilter === 'YES')) return false;
    if (adminFilter !== 'ALL' && isAdminFlag(m) !== (adminFilter === 'YES')) return false;
    if (query) {
      const q = query.toLowerCase();
      const hit = (m.email || '').toLowerCase().includes(q) ||
        (`${m.firstName || ''} ${m.lastName || ''}`).toLowerCase().includes(q) ||
        (m.registrationIp || '').toLowerCase().includes(q) ||
        (m.lastLoginIp || '').toLowerCase().includes(q);
      if (!hit) return false;
    }
    return true;
  }), [members, tierFilter, statusFilter, verifiedFilter, adminFilter, query]);

  // Sort the filtered rows by the active column/direction.
  const sorted = useMemo(() => {
    const val = (m) => {
      switch (sortKey) {
        case 'email': return (m.email || '').toLowerCase();
        case 'name': return `${m.firstName || ''} ${m.lastName || ''}`.trim().toLowerCase();
        case 'membershipType': return (m.membershipType || 'FREE').toLowerCase();
        case 'status': return (m.status || '').toLowerCase();
        case 'emailVerified': return m.emailVerified ? 1 : 0;
        case 'loginCount': return m.loginCount ?? 0;
        case 'lastLoginAt': return m.lastLoginAt ? new Date(m.lastLoginAt).getTime() : 0;
        case 'lastLoginIp': return (m.lastLoginIp || '').toLowerCase();
        case 'createdAt': return m.createdAt ? new Date(m.createdAt).getTime() : 0;
        default: return '';
      }
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    // Copy so we don't mutate the filtered array.
    return [...filtered].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  // Keep selection in sync with the visible set (don't act on hidden rows).
  const visibleIds = useMemo(() => sorted.map((m) => String(m.id)), [sorted]);
  const selectedVisible = useMemo(
    () => visibleIds.filter((id) => selected.has(id)),
    [visibleIds, selected]
  );
  const allVisibleSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // --- Bulk actions (tier/status/verify/delete only — NEVER admin). ---
  // Applies to the currently-selected visible members, skipping self where unsafe.
  const runBulk = async (label, fn, { destructive = false } = {}) => {
    const targets = filtered.filter((m) => selected.has(String(m.id)));
    if (targets.length === 0) { toast.error('No users selected'); return; }
    if (destructive && !window.confirm(`${label} for ${targets.length} selected user(s)? This cannot be undone.`)) return;
    if (!destructive && !window.confirm(`${label} for ${targets.length} selected user(s)?`)) return;

    setBusy(true);
    let ok = 0, skipped = 0, failed = 0;
    for (const m of targets) {
      try {
        const result = await fn(m); // 'skip' | 'ok'
        if (result === 'skip') skipped++; else ok++;
      } catch (_) {
        failed++;
      }
    }
    setBusy(false);
    clearSelection();
    await load();
    toast.success(`${label}: ${ok} updated${skipped ? `, ${skipped} skipped` : ''}${failed ? `, ${failed} failed` : ''}`);
  };

  const bulkSetTier = (tier) => runBulk(
    tier === 'PREMIUM' ? 'Upgrade to Premium' : 'Downgrade to Free',
    async (m) => {
      if ((m.membershipType || 'FREE') === tier) return 'skip';
      await axios.patch(`/admin/members/${m.id}`, { membershipType: tier });
      return 'ok';
    }
  );
  const bulkSetStatus = (status) => runBulk(
    status === 'ACTIVE' ? 'Set Active' : 'Set Pending',
    async (m) => {
      if ((m.status || '') === status) return 'skip';
      await axios.patch(`/admin/members/${m.id}`, { status });
      return 'ok';
    }
  );
  const bulkSetVerified = (verified) => runBulk(
    verified ? 'Verify email' : 'Unverify email',
    async (m) => {
      if ((!!m.emailVerified) === verified) return 'skip';
      await axios.patch(`/admin/members/${m.id}`, { emailVerified: verified });
      return 'ok';
    }
  );
  const bulkDelete = () => runBulk(
    'Delete',
    async (m) => {
      if (String(m.id) === String(user?.id)) return 'skip'; // never delete self
      await axios.delete(`/admin/members/${m.id}`);
      return 'ok';
    },
    { destructive: true }
  );

  const StatCard = ({ label, value, color }) => (
    <div className="bg-white rounded-lg shadow-sm p-4 flex-1 min-w-[110px]">
      <div className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value ?? '—'}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );

  const selectClass = 'px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white';

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50 py-8'}>
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4'}>
        {embedded ? (
          <div className="flex justify-end mb-3">
            <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">
              Refresh
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
              <p className="text-sm text-gray-500">User management &amp; account administration</p>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/chat" className="px-3 py-1.5 text-sm text-purple-700 border border-purple-200 rounded hover:bg-purple-50">
                AI Chat History
              </Link>
              <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Summary cards */}
        <div className="flex flex-wrap gap-3 mb-6">
          <StatCard label="Total" value={stats?.total} />
          <StatCard label="Verified" value={stats?.verified} color="text-green-600" />
          <StatCard label="Unverified" value={stats?.unverified} color="text-gray-500" />
          <StatCard label="Premium" value={stats?.premium} color="text-indigo-600" />
          <StatCard label="Free" value={stats?.free} />
          <StatCard label="Pending" value={stats?.pending} color="text-amber-600" />
        </div>

        <h2 className="text-lg font-semibold text-gray-900 mb-3">User Management</h2>

        {/* Filters + search */}
        <div className="bg-white rounded-lg shadow-sm p-3 mb-3 flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Search email, name, or IP…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 min-w-[220px] px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          />
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className={selectClass}>
            <option value="ALL">All tiers</option>
            <option value="PREMIUM">Premium</option>
            <option value="FREE">Free</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="PENDING">Pending</option>
          </select>
          <select value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)} className={selectClass}>
            <option value="ALL">Verified: any</option>
            <option value="YES">Verified</option>
            <option value="NO">Unverified</option>
          </select>
          <select value={adminFilter} onChange={(e) => setAdminFilter(e.target.value)} className={selectClass}>
            <option value="ALL">Admin: any</option>
            <option value="YES">Admins</option>
            <option value="NO">Non-admins</option>
          </select>
        </div>

        {/* Bulk action toolbar — appears when rows are selected. NO admin toggle here. */}
        {selectedVisible.length > 0 && (
          <div className="bg-blue-600 text-white rounded-lg shadow-sm p-3 mb-3 flex flex-wrap items-center gap-2 sticky top-2 z-20">
            <span className="font-semibold text-sm mr-2">{selectedVisible.length} selected</span>
            <button disabled={busy} onClick={() => bulkSetTier('PREMIUM')} className="px-2.5 py-1 text-xs bg-white/15 hover:bg-white/25 rounded disabled:opacity-50">↑ Premium</button>
            <button disabled={busy} onClick={() => bulkSetTier('FREE')} className="px-2.5 py-1 text-xs bg-white/15 hover:bg-white/25 rounded disabled:opacity-50">↓ Free</button>
            <button disabled={busy} onClick={() => bulkSetStatus('ACTIVE')} className="px-2.5 py-1 text-xs bg-white/15 hover:bg-white/25 rounded disabled:opacity-50">Set Active</button>
            <button disabled={busy} onClick={() => bulkSetStatus('PENDING')} className="px-2.5 py-1 text-xs bg-white/15 hover:bg-white/25 rounded disabled:opacity-50">Set Pending</button>
            <button disabled={busy} onClick={() => bulkSetVerified(true)} className="px-2.5 py-1 text-xs bg-white/15 hover:bg-white/25 rounded disabled:opacity-50">Verify</button>
            <button disabled={busy} onClick={() => bulkSetVerified(false)} className="px-2.5 py-1 text-xs bg-white/15 hover:bg-white/25 rounded disabled:opacity-50">Unverify</button>
            <button disabled={busy} onClick={bulkDelete} className="px-2.5 py-1 text-xs bg-red-500 hover:bg-red-600 rounded disabled:opacity-50">Delete</button>
            <button onClick={clearSelection} className="ml-auto px-2.5 py-1 text-xs bg-white/10 hover:bg-white/20 rounded">Clear</button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading members…</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} aria-label="Select all" />
                  </th>
                  <SortHeader label="Email" sortKey="email" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Name" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Tier" sortKey="membershipType" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" sortKey="status" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Verified" sortKey="emailVerified" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Logins" sortKey="loginCount" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Last login" sortKey="lastLoginAt" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Last IP" sortKey="lastLoginIp" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Created" sortKey="createdAt" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => {
                  const isSelf = String(m.id) === String(user?.id);
                  const admin = isAdminFlag(m);
                  const checked = selected.has(String(m.id));
                  return (
                    <tr key={m.id} className={`border-t border-gray-100 hover:bg-gray-50 ${checked ? 'bg-blue-50' : ''}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={checked} onChange={() => toggleSelect(m.id)} aria-label={`Select ${m.email}`} />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => setDetail(m)}
                          className="text-blue-600 hover:underline font-medium"
                          title="View full details"
                        >
                          {m.email}
                        </button>
                        {admin && <Badge color="bg-purple-100 text-purple-700 ml-1">admin</Badge>}
                        {isSelf && <Badge color="bg-blue-100 text-blue-700 ml-1">you</Badge>}
                      </td>
                      <td className="px-3 py-2">{`${m.firstName || ''} ${m.lastName || ''}`.trim() || '—'}</td>
                      <td className="px-3 py-2">
                        <Badge color={m.membershipType === 'PREMIUM' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}>
                          {m.membershipType || 'FREE'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge color={m.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {m.status || '—'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">{m.emailVerified ? '✓' : '✗'}</td>
                      <td className="px-3 py-2">{m.loginCount ?? 0}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(m.lastLoginAt)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">{m.lastLoginIp || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">{fmtDate(m.createdAt)}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-500">No members match your filters.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-3">
          {filtered.length} of {members.length} members shown. Click an email for full details.
          Actions require admin privileges and are enforced server-side.
        </p>
      </div>

      {/* Detail modal — all member info incl. IP addresses + single-user actions. */}
      {detail && (
        <MemberDetailModal
          member={detail}
          isSelf={String(detail.id) === String(user?.id)}
          busy={busy}
          onClose={() => setDetail(null)}
          onPatch={async (fields, msg) => {
            try { await patchMember(detail.id, fields, msg); setDetail((d) => ({ ...d, ...fields })); } catch (_) {}
          }}
          onToggleAdmin={() => toggleAdmin(detail)}
          onDelete={async () => { await deleteMember(detail.id, detail.email); setDetail(null); }}
        />
      )}
    </div>
  );
};

// --- Member detail modal ---
const Field = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-gray-50">
    <span className="text-gray-500 text-sm">{label}</span>
    <span className={`text-sm text-gray-900 text-right ${mono ? 'font-mono' : 'font-medium'}`}>{value ?? '—'}</span>
  </div>
);

const MemberDetailModal = ({ member: m, isSelf, busy, onClose, onPatch, onToggleAdmin, onDelete }) => {
  const admin = isAdminFlag(m);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email}</h3>
            <p className="text-sm text-gray-500">{m.email}
              {admin && <Badge color="bg-purple-100 text-purple-700 ml-2">admin</Badge>}
              {isSelf && <Badge color="bg-blue-100 text-blue-700 ml-1">you</Badge>}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="p-5">
          <Field label="ID" value={m.id} />
          <Field label="Phone" value={m.phone} />
          <Field label="Tier" value={m.membershipType || 'FREE'} />
          <Field label="Status" value={m.status} />
          <Field label="Email verified" value={m.emailVerified ? 'Yes' : 'No'} />
          <Field label="Verified at" value={fmtDate(m.verifiedAt)} />
          <Field label="Auth provider" value={m.authProvider} />
          <Field label="Login count" value={m.loginCount ?? 0} />
          <Field label="Last login" value={fmtDate(m.lastLoginAt)} />
          <Field label="Registration IP" value={m.registrationIp} mono />
          <Field label="Last login IP" value={m.lastLoginIp} mono />
          <Field label="Stripe customer" value={m.stripeCustomerId} mono />
          <Field label="Subscription status" value={m.subscriptionStatus} />
          <Field label="Created" value={fmtDate(m.createdAt)} />
          <Field label="Updated" value={fmtDate(m.updatedAt)} />
        </div>

        {/* Single-user actions */}
        <div className="p-5 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <div className="flex flex-wrap gap-2 mb-3">
            <button disabled={busy} onClick={() => onPatch({ membershipType: m.membershipType === 'PREMIUM' ? 'FREE' : 'PREMIUM' }, 'Tier updated')}
              className="px-2.5 py-1 text-xs bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 disabled:opacity-50">
              {m.membershipType === 'PREMIUM' ? '↓ Free' : '↑ Premium'}
            </button>
            <button disabled={busy} onClick={() => onPatch({ status: m.status === 'ACTIVE' ? 'PENDING' : 'ACTIVE' }, 'Status updated')}
              className="px-2.5 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
              {m.status === 'ACTIVE' ? 'Set Pending' : 'Set Active'}
            </button>
            <button disabled={busy} onClick={() => onPatch({ emailVerified: !m.emailVerified }, 'Verification updated')}
              className="px-2.5 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
              {m.emailVerified ? 'Unverify' : 'Verify'}
            </button>
            <button disabled={busy || isSelf} onClick={onDelete} title={isSelf ? 'You cannot delete yourself' : ''}
              className="px-2.5 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 disabled:opacity-50">
              Delete
            </button>
          </div>

          {/* Admin grant/revoke — isolated + confirmed, never a bulk action. */}
          <div className="border-t border-gray-200 pt-3">
            <p className="text-xs text-gray-500 mb-2">Privileged action — handle with care.</p>
            <button
              disabled={busy || isSelf}
              onClick={onToggleAdmin}
              title={isSelf ? 'You cannot change your own admin flag' : ''}
              className={`px-3 py-1.5 text-xs font-semibold rounded disabled:opacity-50 ${
                admin ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {admin ? 'Revoke admin access' : 'Grant admin access'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
